import { useId, useState, type FormEvent } from 'react'
import { Ban } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field'
import { FormDialog, FormDialogContent, FormDialogTrigger } from '@/components/ui/form-dialog'
import { Input } from '@/components/ui/input'
import { apiClient } from '@/lib/api/client'
import type { Inquiry } from '@/lib/api/types'
import { quickEditErrorMessage } from '@/lib/quick-edit'
import { personName, useHelmPeople } from '@/src/hooks/useHelmPeople'

/** "22 Oct 2026, 10:30", in Kampala. */
function kampalaDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Africa/Kampala',
  })
}

function phaseName(phase: string) {
  return phase.charAt(0) + phase.slice(1).toLowerCase().replace(/_/g, ' ')
}

/**
 * Across the top of a cancelled inquiry: that it is cancelled, why, by whom
 * and when, and for admins a way to reopen it.
 */
export function InquiryCancelledBanner({
  inquiry,
  canReopen,
  onReopened,
}: {
  inquiry: Pick<
    Inquiry,
    'id' | 'workflow_phase' | 'cancelled_at' | 'cancelled_by' | 'cancellation_reason'
  >
  canReopen: boolean
  onReopened: () => void
}) {
  const id = useId()
  const people = useHelmPeople()
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const who = inquiry.cancelled_by
    ? inquiry.cancelled_by === people.data?.meId
      ? 'you'
      : personName(people.data?.users.get(inquiry.cancelled_by))
    : null
  const when = inquiry.cancelled_at ? kampalaDateTime(inquiry.cancelled_at) : null

  async function reopen(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await apiClient.reopenInquiry(inquiry.id, note.trim() || null)
      setOpen(false)
      onReopened()
    } catch (cause) {
      setError(quickEditErrorMessage(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Alert
      variant="destructive"
      className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      aria-label="This inquiry is cancelled"
    >
      <div className="flex min-w-0 gap-3">
        <Ban aria-hidden className="mt-0.5 size-4 shrink-0" />
        <div className="min-w-0 space-y-1">
          <AlertTitle>Cancelled</AlertTitle>
          <AlertDescription className="text-foreground">
            {inquiry.cancellation_reason ?? 'Helm did not record why.'}
          </AlertDescription>
          {(who || when) && (
            <p className="text-xs text-muted-foreground">
              {['Cancelled', who && `by ${who}`, when && `on ${when}`].filter(Boolean).join(' ')}
            </p>
          )}
        </div>
      </div>
      {canReopen && (
        <FormDialog open={open} onOpenChange={(next) => !busy && setOpen(next)}>
          <FormDialogTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="shrink-0 text-foreground">
              Reopen
            </Button>
          </FormDialogTrigger>
          <FormDialogContent
            size="sm"
            title="Reopen this inquiry?"
            description={`Its work becomes active again, in the ${phaseName(inquiry.workflow_phase)} phase. Tasks closed when it was cancelled stay closed.`}
            submitLabel="Reopen"
            busyLabel="Reopening"
            busy={busy}
            error={error}
            errorTitle="Not reopened"
            onSubmit={reopen}
          >
            <Field>
              <FieldLabel htmlFor={`${id}-note`}>Why reopen it?</FieldLabel>
              <Input
                id={`${id}-note`}
                value={note}
                maxLength={500}
                onChange={(event) => setNote(event.target.value)}
                placeholder="The client wrote back."
                aria-describedby={`${id}-note-help`}
              />
              <FieldDescription id={`${id}-note-help`}>
                Optional; it goes on the timeline.
              </FieldDescription>
            </Field>
          </FormDialogContent>
        </FormDialog>
      )}
    </Alert>
  )
}
